import { isCameraStreamLogsEnabled } from '../config';

const IO_THRESHOLD = 0.01;

class VisibilityTracker {
    #observer;
    #elementToCameraId = new Map();
    #elementVisible = new WeakMap();
    #visibleCounts = new Map();
    #subscribers = new Set();

    constructor() {
        if (typeof IntersectionObserver === 'undefined') {
            this.#log('IntersectionObserver not available; visibility tracking disabled', 'warn');
            return;
        }
        this.#observer = new IntersectionObserver((entries) => this.#onIntersect(entries), { threshold: IO_THRESHOLD });
    }

    observe(element, cameraId) {
        if (!this.#observer || !element || cameraId == null) return;
        if (this.#elementToCameraId.has(element)) return;
        this.#elementToCameraId.set(element, cameraId);
        this.#elementVisible.set(element, false);
        this.#observer.observe(element);
    }

    unobserve(element) {
        if (!this.#observer || !element) return;
        const cameraId = this.#elementToCameraId.get(element);
        if (cameraId == null) return;
        this.#observer.unobserve(element);
        this.#elementToCameraId.delete(element);
        if (this.#elementVisible.get(element)) this.#decrement(cameraId);
        this.#elementVisible.delete(element);
    }

    subscribe(callback) {
        this.#subscribers.add(callback);
        return () => this.#subscribers.delete(callback);
    }

    isVisible(cameraId) {
        return (this.#visibleCounts.get(cameraId) || 0) > 0;
    }

    #onIntersect(entries) {
        for (const entry of entries) {
            const cameraId = this.#elementToCameraId.get(entry.target);
            if (cameraId == null) continue;
            const wasVisible = !!this.#elementVisible.get(entry.target);
            const nowVisible = !!entry.isIntersecting;
            if (wasVisible === nowVisible) continue;
            this.#elementVisible.set(entry.target, nowVisible);
            if (nowVisible) this.#increment(cameraId);
            else this.#decrement(cameraId);
        }
    }

    #increment(cameraId) {
        const before = this.#visibleCounts.get(cameraId) || 0;
        this.#visibleCounts.set(cameraId, before + 1);
        if (before === 0) this.#emit(cameraId, true);
    }

    #decrement(cameraId) {
        const before = this.#visibleCounts.get(cameraId) || 0;
        const after = Math.max(0, before - 1);
        if (after === 0) this.#visibleCounts.delete(cameraId);
        else this.#visibleCounts.set(cameraId, after);
        if (before > 0 && after === 0) this.#emit(cameraId, false);
    }

    #emit(cameraId, isVisible) {
        for (const cb of this.#subscribers) {
            try {
                cb(cameraId, isVisible);
            } catch (err) {
                this.#log(`subscriber error: ${err?.message || err}`, 'error');
            }
        }
    }

    #log(message, level = 'log') {
        if (!isCameraStreamLogsEnabled(level)) return;
        console[level]?.(`[VisibilityTracker] ${message}`);
    }
}

export const visibilityTracker = new VisibilityTracker();

if (import.meta.env.VITE_ENV !== 'production') window.visibilityTracker = visibilityTracker;
